import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  PanelLeft,
  PanelLeftClose,
  Pin,
  Search,
  X,
} from 'lucide-react';
import {
  NAV_GROUPS,
  findNavItemForPathname,
  getNavItemByPath,
  isNavPathActive,
  searchNavItems,
  type NavItem,
} from '../../lib/navConfig';
import {
  getShortcutPaths,
  readNavRecents,
  recordNavVisit,
  reorderNavPins,
  toggleNavPin,
  type NavRecentsState,
} from '../../lib/navRecents';

const SIDEBAR_STORAGE_KEY = 'engine-sidebar-open';
const NAV_GROUPS_EXPANDED_KEY = 'engine-nav-groups-expanded';
/** Auto-close after pointer leaves a deliberately opened sidebar. */
export const SIDEBAR_IDLE_CLOSE_MS = 15_000;
/** Auto-close after accidental hover peek (no click in sidebar). */
export const SIDEBAR_PEEK_CLOSE_MS = 400;

function defaultExpandedGroups(): Record<string, boolean> {
  return Object.fromEntries(NAV_GROUPS.map((g) => [g.title, false]));
}

export function readNavGroupsExpanded(): Record<string, boolean> {
  const defaults = defaultExpandedGroups();
  try {
    const stored = localStorage.getItem(NAV_GROUPS_EXPANDED_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, boolean>;
      return { ...defaults, ...parsed };
    }
  } catch {
    /* ignore */
  }
  persistNavGroupsExpanded(defaults);
  return defaults;
}

export function persistNavGroupsExpanded(groups: Record<string, boolean>): void {
  try {
    localStorage.setItem(NAV_GROUPS_EXPANDED_KEY, JSON.stringify(groups));
  } catch {
    /* ignore */
  }
}

interface AppSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onExpand?: () => void;
  onAutoClose?: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

interface SidebarNavButtonProps {
  item: NavItem;
  active: boolean;
  onNavigate: (path: string) => void;
  pinned?: boolean;
  showPin?: boolean;
  onTogglePin?: (path: string) => void;
  showDragHandle?: boolean;
  isDragging?: boolean;
  onDragHandleStart?: () => void;
  onDragHandleEnd?: () => void;
  onDragOver?: (event: React.DragEvent) => void;
  onDrop?: (event: React.DragEvent) => void;
}

const SidebarNavButton: React.FC<SidebarNavButtonProps> = ({
  item,
  active,
  onNavigate,
  pinned = false,
  showPin = false,
  onTogglePin,
  showDragHandle = false,
  isDragging = false,
  onDragHandleStart,
  onDragHandleEnd,
  onDragOver,
  onDrop,
}) => {
  const Icon = item.icon;
  return (
    <div
      className={`group relative flex items-center ${isDragging ? 'opacity-50' : ''}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {showDragHandle && (
        <span
          draggable
          onDragStart={onDragHandleStart}
          onDragEnd={onDragHandleEnd}
          className="shrink-0 p-1 rounded-md text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          title="Drag to reorder"
          aria-label={`Drag ${item.label}`}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
      )}
      <button
        type="button"
        onClick={() => onNavigate(item.path)}
        className={`flex-1 flex items-center gap-2.5 px-2 py-1.5 text-sm text-left rounded-md transition-colors ${
          active
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-200'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-900 dark:text-gray-100'
        }`}
      >
        <Icon className={`w-4 h-4 shrink-0 ${item.color}`} />
        <span className="truncate">{item.label}</span>
      </button>
      {showPin && onTogglePin && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin(item.path);
          }}
          className={`shrink-0 p-1.5 rounded-md transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100 ${
            pinned
              ? 'text-emerald-600 dark:text-emerald-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          aria-label={pinned ? 'Unpin shortcut' : 'Pin shortcut'}
          title={pinned ? 'Unpin' : 'Pin'}
        >
          <Pin className={`w-3.5 h-3.5 ${pinned ? 'fill-current' : ''}`} />
        </button>
      )}
    </div>
  );
};

const AppSidebar: React.FC<AppSidebarProps> = ({
  isOpen,
  onToggle,
  onExpand,
  onAutoClose,
  isMobileOpen,
  onMobileClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const desktopSidebarRef = useRef<HTMLElement>(null);
  const pointerInSidebarRef = useRef(false);
  const leftSidebarAtRef = useRef<number | null>(null);
  const peekOpenedRef = useRef(false);
  const interactedSincePeekRef = useRef(false);
  const peekCloseTimerRef = useRef<number | null>(null);
  const [expandedGroups, setExpandedGroups] = useState(() => readNavGroupsExpanded());
  const [recentsState, setRecentsState] = useState<NavRecentsState>(() => readNavRecents());
  const [searchQuery, setSearchQuery] = useState('');
  const [dragPinPath, setDragPinPath] = useState<string | null>(null);

  const searchResults = useMemo(
    () => searchNavItems(searchQuery),
    [searchQuery]
  );
  const isSearching = searchQuery.trim().length > 0;
  const shortcuts = getShortcutPaths(recentsState);
  const hasShortcuts = shortcuts.pinned.length > 0 || shortcuts.recent.length > 0;

  useEffect(() => {
    const item = findNavItemForPathname(location.pathname);
    if (item) {
      setRecentsState(recordNavVisit(item.path));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) {
      peekOpenedRef.current = false;
      interactedSincePeekRef.current = false;
      if (peekCloseTimerRef.current != null) {
        window.clearTimeout(peekCloseTimerRef.current);
        peekCloseTimerRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isMobileOpen || !onAutoClose) return;

    const sidebarEl = desktopSidebarRef.current;
    if (!sidebarEl) return;

    const clearPeekCloseTimer = () => {
      if (peekCloseTimerRef.current != null) {
        window.clearTimeout(peekCloseTimerRef.current);
        peekCloseTimerRef.current = null;
      }
    };

    const markInside = () => {
      handleDesktopSidebarPointerEnter();
    };

    const markOutside = () => {
      handleDesktopSidebarPointerLeave();
    };

    const onSidebarInteract = () => {
      interactedSincePeekRef.current = true;
      peekOpenedRef.current = false;
      clearPeekCloseTimer();
    };

    const onFocusIn = (event: FocusEvent) => {
      if (sidebarEl.contains(event.target as Node)) {
        markInside();
      }
    };

    const onFocusOut = (event: FocusEvent) => {
      if (!sidebarEl.contains(event.relatedTarget as Node)) {
        markOutside();
      }
    };

    sidebarEl.addEventListener('pointerenter', markInside);
    sidebarEl.addEventListener('pointerleave', markOutside);
    sidebarEl.addEventListener('pointerdown', onSidebarInteract);
    sidebarEl.addEventListener('focusin', onFocusIn);
    sidebarEl.addEventListener('focusout', onFocusOut);

    if (sidebarEl.matches(':hover')) {
      markInside();
    } else {
      markOutside();
    }

    const intervalId = window.setInterval(() => {
      if (pointerInSidebarRef.current) return;
      if (peekOpenedRef.current && !interactedSincePeekRef.current) return;
      if (
        leftSidebarAtRef.current != null &&
        Date.now() - leftSidebarAtRef.current >= SIDEBAR_IDLE_CLOSE_MS
      ) {
        onAutoClose();
      }
    }, 1000);

    return () => {
      sidebarEl.removeEventListener('pointerenter', markInside);
      sidebarEl.removeEventListener('pointerleave', markOutside);
      sidebarEl.removeEventListener('pointerdown', onSidebarInteract);
      sidebarEl.removeEventListener('focusin', onFocusIn);
      sidebarEl.removeEventListener('focusout', onFocusOut);
      clearPeekCloseTimer();
      window.clearInterval(intervalId);
    };
  }, [isOpen, isMobileOpen, onAutoClose]);

  const handleDesktopSidebarPointerEnter = () => {
    pointerInSidebarRef.current = true;
    leftSidebarAtRef.current = null;
    if (peekCloseTimerRef.current != null) {
      window.clearTimeout(peekCloseTimerRef.current);
      peekCloseTimerRef.current = null;
    }

    if (!isOpen && onExpand) {
      peekOpenedRef.current = true;
      interactedSincePeekRef.current = false;
      onExpand();
    }
  };

  const handleDesktopSidebarPointerLeave = () => {
    pointerInSidebarRef.current = false;

    if (peekOpenedRef.current && !interactedSincePeekRef.current && onAutoClose) {
      if (peekCloseTimerRef.current != null) {
        window.clearTimeout(peekCloseTimerRef.current);
      }
      peekCloseTimerRef.current = window.setTimeout(() => {
        peekCloseTimerRef.current = null;
        peekOpenedRef.current = false;
        onAutoClose();
      }, SIDEBAR_PEEK_CLOSE_MS);
      return;
    }

    leftSidebarAtRef.current = Date.now();
  };

  const handleToggle = () => {
    peekOpenedRef.current = false;
    interactedSincePeekRef.current = true;
    onToggle();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onMobileClose();
    setSearchQuery('');
  };

  const handleTogglePin = (path: string) => {
    setRecentsState(toggleNavPin(path));
  };

  const handlePinnedDrop = (targetPath: string) => {
    if (!dragPinPath || dragPinPath === targetPath) {
      setDragPinPath(null);
      return;
    }
    const pinned = shortcuts.pinned;
    const from = pinned.indexOf(dragPinPath);
    const to = pinned.indexOf(targetPath);
    if (from < 0 || to < 0) {
      setDragPinPath(null);
      return;
    }
    setRecentsState(reorderNavPins(from, to));
    setDragPinPath(null);
  };

  const toggleGroup = (title: string) => {
    setExpandedGroups((prev) => {
      const willExpand = !(prev[title] ?? false);
      const next = defaultExpandedGroups();
      if (willExpand) {
        next[title] = true;
      }
      persistNavGroupsExpanded(next);
      return next;
    });
  };

  const renderPinnedSection = (paths: string[]) => {
    if (paths.length === 0) return null;
    return (
      <div className="mb-2">
        <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Pinned
        </div>
        <div className="flex flex-col">
          {paths.map((path) => {
            const item = getNavItemByPath(path);
            if (!item) return null;
            return (
              <SidebarNavButton
                key={`pinned-${path}`}
                item={item}
                active={isNavPathActive(location.pathname, path)}
                onNavigate={handleNavigate}
                pinned
                showPin
                onTogglePin={handleTogglePin}
                showDragHandle
                isDragging={dragPinPath === path}
                onDragHandleStart={() => setDragPinPath(path)}
                onDragHandleEnd={() => setDragPinPath(null)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  handlePinnedDrop(path);
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const renderRecentSection = (paths: string[]) => {
    if (paths.length === 0) return null;
    return (
      <div className="mb-2">
        <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Recent
        </div>
        <div className="flex flex-col">
          {paths.map((path) => {
            const item = getNavItemByPath(path);
            if (!item) return null;
            return (
              <SidebarNavButton
                key={`recent-${path}`}
                item={item}
                active={isNavPathActive(location.pathname, path)}
                onNavigate={handleNavigate}
                pinned={recentsState.pinned.includes(path)}
                showPin
                onTogglePin={handleTogglePin}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="px-3 pt-3 pb-2 shrink-0 border-b border-gray-100 dark:border-gray-700">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search navigation…"
            className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            aria-label="Search navigation"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Sized to its content: both headings plus the default pins and three recents fit
          without a scrollbar. The cap stays because pinning is unbounded — the group nav
          below must not get pushed off screen by a long pin list. */}
      {!isSearching && hasShortcuts && (
        <div className="px-3 py-2 shrink-0 border-b border-gray-100 dark:border-gray-700 max-h-[50vh] overflow-y-auto">
          {renderPinnedSection(shortcuts.pinned)}
          {renderRecentSection(shortcuts.recent)}
        </div>
      )}

      <nav className="flex-1 min-h-0 overflow-y-auto" aria-label="Main navigation">
        {isSearching ? (
          <div className="px-3 py-3">
            <div className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Results
            </div>
            {searchResults.length === 0 ? (
              <p className="px-2 text-sm text-gray-500 dark:text-gray-400">No matches</p>
            ) : (
              <div className="flex flex-col">
                {searchResults.map((item) => (
                  <SidebarNavButton
                    key={item.path}
                    item={item}
                    active={isNavPathActive(location.pathname, item.path)}
                    onNavigate={handleNavigate}
                    pinned={recentsState.pinned.includes(item.path)}
                    showPin
                    onTogglePin={handleTogglePin}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          NAV_GROUPS.map((group) => {
            const GroupIcon = group.icon;
            const isExpanded = expandedGroups[group.title] ?? false;
            return (
              <div key={group.title} className="border-t border-gray-200 dark:border-gray-700">
                <div className="px-3 pt-2">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.title)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 mb-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    aria-expanded={isExpanded}
                    aria-controls={`nav-group-${group.title}`}
                  >
                    <GroupIcon className={`w-4 h-4 shrink-0 ${group.color}`} />
                    <span className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 text-left">
                      {group.title}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                    )}
                  </button>
                  {isExpanded && (
                    <div
                      id={`nav-group-${group.title}`}
                      className="flex flex-col pb-2"
                    >
                      {group.items.map((item) => (
                        <SidebarNavButton
                          key={item.path}
                          item={item}
                          active={isNavPathActive(location.pathname, item.path)}
                          onNavigate={handleNavigate}
                          pinned={recentsState.pinned.includes(item.path)}
                          showPin
                          onTogglePin={handleTogglePin}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </nav>
    </div>
  );

  const desktopSidebarToggle = (
    <div
      className={`mt-auto shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 z-10 ${
        isOpen ? 'px-3 py-2' : 'px-1 py-2'
      }`}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={`hidden lg:flex w-full items-center rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          isOpen ? 'justify-start px-2 py-2' : 'justify-center p-2'
        }`}
        aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        {isOpen ? (
          <PanelLeftClose className="w-5 h-5 shrink-0" />
        ) : (
          <PanelLeft className="w-5 h-5 shrink-0" />
        )}
      </button>
    </div>
  );

  const sidebarContentWithMobileFooter = (
    <>
      {sidebarContent}
      <div className="mt-auto shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 lg:hidden">
        <button
          type="button"
          onClick={onMobileClose}
          className="w-full flex items-center justify-start px-2 py-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Close navigation"
          title="Close navigation"
        >
          <PanelLeftClose className="w-5 h-5 shrink-0" />
        </button>
      </div>
    </>
  );

  return (
    <>
      {isMobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={onMobileClose}
          aria-label="Close navigation overlay"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden flex flex-col ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-hidden={!isMobileOpen}
      >
        {sidebarContentWithMobileFooter}
      </aside>

      {/* Full-height rail paints bg for the whole column; sticky nav sits inside.
          Without this, self-start + viewport-tall sticky leaves a gray-900 gap when scrolling. */}
      <div
        className={`hidden lg:flex shrink-0 flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-[width] duration-500 ease-in-out ${
          isOpen ? 'w-64' : 'w-10'
        }`}
      >
        <aside
          ref={desktopSidebarRef}
          onPointerEnter={handleDesktopSidebarPointerEnter}
          onPointerLeave={handleDesktopSidebarPointerLeave}
          className="sticky top-16 flex flex-col h-[calc(100vh-4rem)] w-full overflow-hidden"
        >
          {isOpen ? (
            <div className="w-64 flex-1 min-h-0 flex flex-col overflow-hidden">
              {sidebarContent}
            </div>
          ) : (
            <div className="flex-1 min-h-0" aria-hidden="true" />
          )}
          {desktopSidebarToggle}
        </aside>
      </div>
    </>
  );
};

export function readSidebarOpenPreference(): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored === 'false') return false;
    if (stored === 'true') return true;
  } catch {
    /* ignore */
  }
  return true;
}

export function persistSidebarOpenPreference(isOpen: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isOpen));
  } catch {
    /* ignore */
  }
}

export default AppSidebar;
